import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, eur, offerTotals } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileText, Trash2, ChevronRight } from "lucide-react";

const STAGES = ["Lead", "Contatto", "Preventivo", "Vinto", "Perso"];
const STAGE_COLORS = { Lead: "#94A3B8", Contatto: "#0EA5E9", Preventivo: "#F59E0B", Vinto: "#10B981", Perso: "#EF4444" };
const emptyForm = { title: "", company_id: "", contact_id: "", stage: "Lead", items: [], discount_pct: 0, tax_pct: 22, notes: "", valid_until: "" };

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/offers").then((r) => setOffers(r.data));
    api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (form.company_id) api.get(`/contacts?company_id=${form.company_id}`).then((r) => setContacts(r.data));
    else setContacts([]);
  }, [form.company_id]);

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  const addProduct = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    setForm((f) => ({ ...f, items: [...f.items, { product_id: p.id, description: p.model + (p.category ? ` — ${p.category}` : ""), quantity: 1, unit_price: p.price }] }));
  };
  const addCustom = () => setForm((f) => ({ ...f, items: [...f.items, { product_id: "", description: "", quantity: 1, unit_price: 0 }] }));
  const updItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const delItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titolo obbligatorio"); return; }
    if (!form.company_id) { toast.error("Seleziona un'azienda"); return; }
    const payload = {
      ...form, discount_pct: Number(form.discount_pct) || 0, tax_pct: Number(form.tax_pct) || 0,
      items: form.items.map((i) => ({ ...i, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0 })),
    };
    try { const { data } = await api.post("/offers", payload); toast.success("Offerta creata"); setOpen(false); setForm(emptyForm); load(); navigate(`/offerte/${data.id}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const totals = offerTotals(form);

  return (
    <div className="p-10">
      <PageHeader subtitle="COMMERCIALE" title="Offerte"
        action={<Button data-testid="new-offer-btn" onClick={() => { setForm(emptyForm); setOpen(true); }} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm"><Plus className="w-4 h-4 mr-2" />Crea Offerta</Button>} />

      {offers.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm py-20 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-4" />
          <div className="font-display font-bold text-lg text-slate-900">Nessuna offerta</div>
          <p className="text-sm text-slate-500 mt-1">Crea la tua prima offerta commerciale.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 bg-slate-50">
              {["Numero", "Titolo", "Azienda", "Fase", "Totale", ""].map((h, i) => (
                <th key={i} className={`font-mono-label text-[10px] text-slate-500 px-6 py-3 ${i === 4 ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {offers.map((o, i) => (
                <tr key={o.id} data-testid={`offer-row-${o.id}`} onClick={() => navigate(`/offerte/${o.id}`)}
                  className={`cursor-pointer hover:bg-slate-50 transition-colors duration-150 ${i % 2 ? "bg-slate-50/40" : ""}`}>
                  <td className="px-6 py-4 font-mono-label text-[10px] text-slate-500">{o.number}</td>
                  <td className="px-6 py-4 font-medium text-slate-900">{o.title}</td>
                  <td className="px-6 py-4 text-slate-600">{companyName(o.company_id)}</td>
                  <td className="px-6 py-4"><span className="text-[10px] px-2 py-0.5 rounded-sm text-white font-medium" style={{ background: STAGE_COLORS[o.stage] }}>{o.stage}</span></td>
                  <td className="px-6 py-4 text-right font-semibold text-slate-900">{eur(offerTotals(o).total)}</td>
                  <td className="px-6 py-4 text-slate-400"><ChevronRight className="w-4 h-4" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Crea Offerta</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label className="font-mono-label text-[10px] text-slate-500">Titolo *</Label>
                <Input data-testid="offer-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 rounded-sm" placeholder="es. Fornitura attuatori linea produzione" /></div>
              <div><Label className="font-mono-label text-[10px] text-slate-500">Azienda *</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v, contact_id: "" })}>
                  <SelectTrigger data-testid="offer-company-select" className="mt-1.5 rounded-sm"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="font-mono-label text-[10px] text-slate-500">Referente</Label>
                <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })} disabled={!form.company_id}>
                  <SelectTrigger data-testid="offer-contact-select" className="mt-1.5 rounded-sm"><SelectValue placeholder="Seleziona referente" /></SelectTrigger>
                  <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="font-mono-label text-[10px] text-slate-500">Fase</Label>
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger data-testid="offer-stage-select" className="mt-1.5 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="font-mono-label text-[10px] text-slate-500">Valida fino al</Label>
                <Input data-testid="offer-valid-input" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="mt-1.5 rounded-sm" /></div>
            </div>

            <div className="border border-slate-200 rounded-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono-label text-[10px] text-slate-500">RIGHE OFFERTA</div>
                <div className="flex gap-2">
                  <Select onValueChange={addProduct}>
                    <SelectTrigger data-testid="offer-add-product-select" className="h-8 rounded-sm text-xs w-48"><SelectValue placeholder="+ Da catalogo" /></SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.model} — {eur(p.price)}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button data-testid="offer-add-custom-btn" type="button" size="sm" variant="outline" onClick={addCustom} className="h-8 rounded-sm text-xs"><Plus className="w-3 h-3 mr-1" />Riga libera</Button>
                </div>
              </div>
              {form.items.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">Aggiungi prodotti dal catalogo o righe libere.</p> : (
                <div className="space-y-2">
                  {form.items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <Input data-testid={`item-desc-${i}`} value={it.description} onChange={(e) => updItem(i, "description", e.target.value)} placeholder="Descrizione" className="col-span-6 rounded-sm h-9 text-sm" />
                      <Input data-testid={`item-qty-${i}`} type="number" value={it.quantity} onChange={(e) => updItem(i, "quantity", e.target.value)} placeholder="Qtà" className="col-span-2 rounded-sm h-9 text-sm" />
                      <Input data-testid={`item-price-${i}`} type="number" value={it.unit_price} onChange={(e) => updItem(i, "unit_price", e.target.value)} placeholder="Prezzo" className="col-span-3 rounded-sm h-9 text-sm" />
                      <button data-testid={`item-del-${i}`} type="button" onClick={() => delItem(i)} className="col-span-1 text-slate-300 hover:text-red-500 transition-colors duration-200 flex justify-center"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div><Label className="font-mono-label text-[10px] text-slate-500">Sconto %</Label>
                <Input data-testid="offer-discount-input" type="number" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} className="mt-1.5 rounded-sm" /></div>
              <div><Label className="font-mono-label text-[10px] text-slate-500">IVA %</Label>
                <Input data-testid="offer-tax-input" type="number" value={form.tax_pct} onChange={(e) => setForm({ ...form, tax_pct: e.target.value })} className="mt-1.5 rounded-sm" /></div>
              <div className="flex flex-col justify-end">
                <div className="font-mono-label text-[10px] text-slate-500">TOTALE</div>
                <div className="font-display font-bold text-xl text-[#0052CC]">{eur(totals.total)}</div>
              </div>
            </div>

            <div><Label className="font-mono-label text-[10px] text-slate-500">Note</Label>
              <Textarea data-testid="offer-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Annulla</Button>
            <Button data-testid="save-offer-btn" onClick={save} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm">Salva Offerta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
